import { html, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import Dropdown from '~/ui/components/dropdown';
import '~/ui/components/dropdown';

import { SalesSite, SalesSiteDropDownItem } from './types';
import DERFormElement from '~/ui/utils/der-form-element';

export class SalesSites extends DERFormElement {
    @property({ type: String, attribute: 'lang' })
    override lang: string = '';

    @property({ type: String, attribute: 'name' })
    name: string = '';

    @property({ type: String, attribute: 'label' })
    label: string = '';

    @property({ type: String, attribute: 'placeholder' })
    placeholder: string = '';

    @property({ type: Boolean, attribute: 'required' })
    required: boolean = false;

    @property({ type: String, attribute: 'selected-team-id' })
    selectedTeamId: string = '';

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = '';

    @state()
    private salesSitesData: SalesSite[] = [];

    @query('der-ui-dropdown') dropdownElement?: Dropdown;

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override connectedCallback(): void {
        super.connectedCallback();
        this._getSalesSitesData();
        this.updateComplete.then(() => {
            this._checkInputValidity();
        });
    }

    override async updated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        // React when language OR endpoint changes (Storybook control updates attribute)
        if ((_changedProperties.has('lang') || _changedProperties.has('salesSiteUrl')) && this.salesSiteUrl) {
            await this._getSalesSitesData();
        }
    }

    private async _getSalesSitesData(): Promise<void> {
        if (!this.salesSiteUrl) {
            this.salesSitesData = [];
            return;
        }

        const url: URL = new URL(this.salesSiteUrl);
        url.searchParams.set('lang', this.lang ?? '');

        const response: Response = await fetch(url.toString());
        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-sites data',
            });
            this.salesSitesData = [];
            return;
        }
        const data: unknown = await response.json();
        this.salesSitesData = data as SalesSite[];
    }

    get dropdownItems(): SalesSiteDropDownItem[] {
        if (!Array.isArray(this.salesSitesData) || !this.salesSitesData.length) return [];
        return this.salesSitesData
            .filter((item: SalesSite): boolean => !!item.teamId)
            .map((item: SalesSite): SalesSiteDropDownItem => {
                const { zip, address, city, name } = item;
                const parts: unknown[] = [zip, city, address, name].filter(Boolean);
                return {
                    name: parts.join(' - '),
                    id: item.teamId,
                };
            });
    }

    private _handleChange(event: CustomEvent<{ value: string }>): void {
        event.stopPropagation();
        this.selectedTeamId = event.detail.value;
        this._internals.setFormValue(this.selectedTeamId);
        this._checkInputValidity();

        this.dispatchEvent(
            new CustomEvent('sales-site-selected', {
                detail: {
                    salesSite: this.selectedTeamId,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    private _handleRendered(): void {
        this._checkInputValidity();
    }

    private _checkInputValidity(): void {
        if (!this.dropdownElement?.selectEl) {
            return;
        }
        this.setDERValidity(
            this.dropdownElement.selectEl.validity,
            this.dropdownElement.selectEl.validationMessage,
            this.dropdownElement.selectEl
        );
    }

    override render(): TemplateResult {
        return html`
            <der-ui-dropdown
                label="${this.label}"
                name="${this.name}"
                @der.change=${this._handleChange}
                @rendered=${this._handleRendered}
                .options=${this.dropdownItems}
                placeholder="${this.placeholder}"
                ?required="${this.required}"
            ></der-ui-dropdown>
        `;
    }
}
